<template>
  <section>
    <!-- SB-43 -->
    <banner-full-width headerText="Equipment"></banner-full-width>
    <div
      id="sb-43"
      v-if="$store.state.currentLocation >= 43"
    >
      <content-block>
        <!-- 5-01-00 -->
        <!-- <banner-full-width headerText=""></banner-full-width> -->
        <!-- <h1 class="animated infinite bounce delay-2s">Example</h1> -->
        <h2 class="content-block__header content-block__header--red">Up next</h2>
        <p>In this section, we will identify the safety equipment that you need to have in a rowing shell, a coach boat, and at the boathouse, and we’ll discuss how you can ensure that it is safe to use.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="44"
          link="sb-44"
        >
          Tell me more
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-44 -->
    <div
      id="sb-44"
      v-if="$store.state.currentLocation >= 44"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Check the rowing shell </h2>
        <p>It’s a best practice in rowing to check all items you’ll bring on the water, so let’s start with your shell. Place it on stretchers for a thorough check before heading to the dock.</p>
        <ul class="chevron-list">
          <li>Check that the hardware holding the rigger to the shell is tight and not missing. Ideally, check for tightness with a wrench.</li>
          <li>Ensure oarlocks are in good working order with no cracks or significant wear.</li>
          <li>Ensure that the oarlock top nut is tight.</li>
          <li>Run your hand over the hull to feel for cracks inside and out, as well as the decks.</li>
          <li>If there is tape covering a crack or check (commonly used for quick fixes), ensure it is still secure with no edges lifting.</li>
          <li>Check that the vent caps are securely installed and not cross-threaded, which would allow water into the watertight compartments.</li>
          <li>Set the speed washers, if you know the oarlock height that you want (they set the oar height). </li>
        </ul>
        <p>Note that the speed washer height may differ between people. It’s easier to adjust them off the water, while you check your shell, but it can be done while on the water, if necessary.</p>
        <p>If any nuts and bolts on the riggers need replacement and you are confident about replacing them, then do so. Often there is a bin with common nuts, bolts and wrenches at the boathouse, so that can be done easily before going on the water.</p>
      </content-block>
      <content-block class="bg-img bg-img--01">
        <h3 class="text-red">Safety equipment in the shell</h3>
        <p>All boats, including rowing shells, must meet Transport Canada Guidelines. </p>
        <p>A rowing shell must have navigation lights—including a bow light, preferably red and green, and a white stern light—if operated one hour before sunrise or one hour after sunset.</p>
        <p>Shells must also carry a PFD for each rower in the shell, a sound signalling device (whistle) and a watertight light, unless a coach boat is accompanying the shell.</p>
        <p>It is your responsibility as a rower to learn the Transport Canada Guidelines. Review them each season as a reminder.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="45"
          link="sb-45"
        >
          Will do
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-45 -->
    <div
      id="sb-45"
      v-if="$store.state.currentLocation >= 45"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Check the foot stretchers </h2>
        <div class="row">
          <div class="col-md-4 text-red">
            <p>Foot stretchers are an important safety feature. While they join or anchor the rower to the shell, they also allow you to pull your feet out easily if the shell capsizes.</p>
            <p>When checking the foot stretchers:</p>
          </div>
          <div class="col-md-4 col-sm-6">
            <ul class="grid-list">
              <li>Ensure the foot stretcher hardware (wing nuts and bolts) is tight.</li>
              <li>Adjust the foot stretchers to your requirements.</li>
            </ul>
          </div>
          <div class="col-md-4 col-sm-6">
            <ul class="grid-list grid-list--border-left">
              <li>Check that the heels are tied down and do not raise from the bottom of the foot stretcher by more than 7 cm (three to four fingers).</li>
              <li>Test that the closures can be released with a single quick hand action of pulling on one easily accessible strap.</li>
            </ul>
          </div>
        </div>
        <p class="pt-3">When you need to replace foot stretchers, replace and tighten them yourself only if you’re confident in your ability to do it.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="46"
          link="sb-46"
        >
          I’ll check the foot stretchers
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-46 -->
    <div
      id="sb-46"
      v-if="$store.state.currentLocation >= 46"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Check the seat </h2>
        <p>Your seat in the shell should be comfortable and functional. </p>
        <p>To check the shell seat:</p>
        <ul class="chevron-list">
          <li>Check that your seat rolls easily and smoothly. </li>
          <li>Adjust the seat slide, if necessary.</li>
          <li>Ensure that the seat slides are even, to avoid the seat hitting the front or back of the slides while rowing.</li>
          <li>Check the tightness of the slide nuts. You can do this on the water, but it is much easier when the shell is on stretchers.</li>
        </ul>
        <p>If you know how to replace and tighten any parts or pieces of your seat, do that before you get on the water.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="47"
          link="sb-47"
        >
          I’ll check the seat
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-47 -->
    <div
      id="sb-47"
      v-if="$store.state.currentLocation >= 47"
    >
      <content-block class="bg-img bg-img--02">
        <h2 class="content-block__header content-block__header--red">Check the fin and bow ball </h2>
        <p>The fin and the bow ball are attachments to your shell. The fin helps the shell track in a straight line; it also helps with its balance. </p>
        <p>The bow ball reduces the risk of puncturing something or someone and prevents damage to the bow tips of rowing shells. </p>
        <p>When checking your shell ensure that:</p>
        <div class="row">
          <div class="col-sm bg-red text-center p-4 m-3">
            <p>The fin is firmly affixed and straight with the hull spine.</p>
          </div>
          <div class="col-sm bg-red text-center p-4 m-3">
            <p>The bow ball is firmly attached and has no cracks or splits in it.</p>
          </div>
          <div class="col-sm bg-red text-center p-4 m-3">
            <p>The bow and stern compartment vents are closed and tight.</p>
          </div>
        </div>
        <button-sequenced
          class="btn-nav--red"
          :loc="48"
          link="sb-48"
        >
          I’ll check the fin and bow ball
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-48 -->
    <div
      id="sb-48"
      v-if="$store.state.currentLocation >= 48"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Check the oars </h2>
        <div class="row">
          <div class="col-md-4 text-red">
            <p>Always check over the sculls or oars before heading out to the water. While they’re not part of your shell, they are part of rowing. </p>
          </div>
          <div class="col-md-4 col-sm-6">
            <ul class="grid-list">
              <li>Check for cracks or checks in the shaft and blade.</li>
              <li>Take a look at the grips to ensure they are clean and not worn. </li>
            </ul>
          </div>
          <div class="col-md-4 col-sm-6">
            <ul class="grid-list grid-list--border-left">
              <li>Check that the collar on the oar is secure and tight.</li>
              <li>Check that all the screws and bolts are tight if the oar or scull is adjustable. </li>
            </ul>
          </div>
        </div>
        <button-sequenced
          class="btn-nav--red"
          :loc="49"
          link="sb-49"
        >
          I’ll check the oars
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-49 -->
    <div
      id="sb-49"
      v-if="$store.state.currentLocation >= 49"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Check the safety equipment in a coach or safety boat </h2>
        <p>At all times, a coach or safety boat must have:</p>
        <ul class="chevron-list">
          <li>An approved PFD for each coach boat occupant, as well as each person in the largest shell under their control</li>
          <li>A buoyant heaving line</li>
          <li>A paddle</li>
          <li>A sound signalling device</li>
          <li>A bailer</li>
          <li>A watertight flashlight or flares</li>
          <li>Navigation lights, if operated before sunrise or after sunset</li>
          <li>Thermal blankets</li>
        </ul>
        <p>Transport Canada details this list in the online document <em>Safe Boating Guide</em>, which you can find on the RCA website.</p>
        <button-sequenced
          class="btn-nav--red"
          :loc="50"
          link="sb-50"
        >
          I’ll review the Guide
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-50 -->
    <div
      id="sb-50"
      class="bg-img bg-img--04"
      v-if="$store.state.currentLocation >= 50"
    >
      <content-block>
        <h2 class="content-block__header content-block__header--red">Check the safety resources at the boathouse </h2>
        <p>Clubs are recommended to take the following safety precautions:</p>
        <ul class="chevron-list">
          <li>Share the Emergency Action Plan location and ensure members read it so that they are aware of what to do in an emergency.</li>
          <li>Display the location of the first aid kit and ensure it stays stocked at all times. Keep the club address visible, to give 911 operators the right information.</li>
          <li>Keep the automatic external defibrillator (AED)—if there is one—visible and train several members in its operation.</li>
          <li>Display the throw ring—if there is one—at the dock, in case it needs to be used.</li>
          <li>Keep a stock of thermal and wool blankets and ensure members know where they’re stored.</li>
          <li>Have a repair book or board available for members to enter any equipment repair needed (to avoid broken equipment being used and to have items promptly repaired).</li>
          <li>Consider an Out/In Log for use before and after rowing.</li>
        </ul>
        <button-sequenced
          class="btn-nav--red"
          :loc="51"
          link="sb-51"
        >
          Makes sense
        </button-sequenced>
      </content-block>
    </div>

    <!-- SB-51 -->
    <div
      id="sb-51"
      v-if="$store.state.currentLocation >= 51"
    >
      <content-block class="quiz-bg">
        <h2 class="content-block__header ">Got everything?</h2>
        <quiz-radio
          :questions="q01"
          @quiz-complete="
                $store.dispatch('showNext', { n: 52, el: '#menu' })
              "
        ></quiz-radio>
      </content-block>
    </div>

    <footer
      class="course-menu"
      id="menu"
      v-if="$store.state.currentLocation >= 52"
    >
      <h3>Course Menu</h3>
      <cac-menu :menuItems="$store.state.menuItems"></cac-menu>
    </footer>
  </section>
</template>

<script>
// @ is an alias to /src
// import HelloWorld from '@/components/HelloWorld.vue'
import CacHeader from '@/components/cac-components/CacHeader.vue';
import CacMenu from '@/components/cac-components/CacMenu.vue';
import BannerFullWidth from '@/components/BannerFullWidth.vue';
import ContentBlock from '@/components/ContentBlock.vue';
import ButtonSequenced from '@/components/ButtonSequenced.vue';
import AccordionIcon from '@/components/AccordionIcon.vue';
import Accordion from '@/components/Accordion.vue';

// Quiz stuff
import QuizRadio from '@/components/QuizRadio.vue';
import QuizCheckbox from '@/components/QuizCheckbox.vue';

export default {
  name: 'nccp',
  components: {
    CacHeader,
    CacMenu,
    BannerFullWidth,
    ContentBlock,
    ButtonSequenced,
    AccordionIcon,
    Accordion,
    QuizRadio
  },
  data() {
    return {
      showModal: false,
      // 3-01-03
      q01: [
        // Q1
        {
          id: 0,
          questionText: `
            <p class="text-key-take">It is late autumn; you and your crewmates arrive for an early morning training session and prepare your shell for an outing without direct coaching supervision. </p>
            <p class="text-key-take">What equipment <strong>must</strong> be in the shell?</p>
          `,
          questionArray: [
            'PFDs, water bottles, wrenches, warm clothing',
            'PFDs, a water bailer, water bottles, navigation lights',
            'PFDs, a whistle, watertight lights',
            'A paddle, a water bailer, a towline, thermal blankets'
          ],
          corrective: 'PFDs, a whistle, watertight lights',
          model: [],
          answer: '',
          answerText: `
            <p>Water bottles, wrenches and warm clothing are recommended, but not a requirement for rowing shells. PFDs, a sound signalling device and navigation or watertight lights are Transport Canada requirements.</p>
          `,
        },
      ],
    };
  },
  methods: {
    tabContent(index) {
      this.tabIndex = index;
    },
  },
};
</script>

<style lang="scss" scoped>
/*------------------------------------*\
    #PRIMARY COLOURS
  \*------------------------------------*/

/*------------------------------------*\
    #Mixins
  \*------------------------------------*/

/*------------------------------------
    #MEDIA SCREENS < 768px (#MOBILE FIRST)
  \*------------------------------------*/
.banner {
  // background-image: url('../assets/img/_chapter-headers/mbl/01-welcome-mbl.png');
  background-image: url("../assets/img/_chapter-headers/mbl/7-equipment-mbl.jpg");
  color: #000;
  background-position: center;
  padding: 60% 10%;
  &__header {
    color: #000;
  }
}

/*------------------------------------*\
    #MEDIA SCREENS > 576px (TABLETS)
  \*------------------------------------*/
@media (min-width: 576px) {
  .banner {
    // background-image: url('../assets/img/_chapter-headers/dsk/01-welcome-dsk.png');
    background-image: url("../assets/img/_chapter-headers/dsk/7-equipment-dsk.jpg");
    color: #000;
    padding: 10% 10% 14%;
    background-position: center;
    &__header {
      color: #000;
    }
  }
}

/*------------------------------------*\
  #MEDIA SCREENS > 767px (TABLETS)
\*------------------------------------*/
@media (min-width: 767px) {
  // .banner {
  //   background-image: url('../assets/img/_bg/1-01-01-bg-dsk.png');
  //   color: #fff;
  //   padding: 10% 10%;
  //   background-position: center;
  //   &__header {
  //       color: #fff;
  //   }
  // }
}

/*------------------------------------*\
    #MEDIA SCREENS > 992px
    (LAPTOP, TABLET)
  \*------------------------------------*/
@media (min-width: 992px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1200px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1200px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1320px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1320px) {
}

/*------------------------------------*\
    #MEDIA SCREENS > 1600px (DESKTOP)
  \*------------------------------------*/
@media (min-width: 1600px) {
}
</style>
